import { motion } from "framer-motion";
import { Mail, LifeBuoy, ShieldAlert, Scale, Copyright } from "lucide-react";
import { CONTACT_EMAIL } from "@/constants/site";

const channels = [
  { icon: LifeBuoy, t: "General support", d: "Questions about your account, coins, gifts or the app.", subject: "Support request" },
  { icon: ShieldAlert, t: "Report & safety", d: "Report content, a creator or a safety concern.", subject: "Safety report" },
  { icon: Scale, t: "Privacy & data", d: "Access, correct or delete your personal data.", subject: "Privacy request" },
  { icon: Copyright, t: "Copyright / DMCA", d: "Report content that infringes your rights.", subject: "Copyright notice" },
];

export default function Contact() {
  return (
    <div data-testid="contact-page">
      <section className="px-6 pb-24 pt-32 md:px-12 md:pt-44">
        <div className="mx-auto max-w-4xl">
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} className="text-center">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-aura-pink">Contact</p>
            <h1 className="mt-4 font-heading text-4xl font-semibold tracking-tighter text-aura-ink sm:text-6xl">
              We're here to help.
            </h1>
            <p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-aura-text2">
              Reach the AuraLive team any time. Pick the topic that fits best —
              it helps us route your message to the right place.
            </p>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              data-testid="contact-primary-email"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-aura-ink px-7 py-3.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-aura-ink2"
            >
              <Mail size={17} /> {CONTACT_EMAIL}
            </a>
          </motion.div>

          <div className="mt-16 grid gap-6 sm:grid-cols-2">
            {channels.map((c, i) => (
              <motion.a
                key={c.t}
                href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(c.subject)}`}
                data-testid={`contact-channel-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="group rounded-3xl border border-black/[0.06] bg-white p-8 solid-card transition-all hover:-translate-y-1 hover:border-black/20"
              >
                <span className="grid h-11 w-11 place-items-center rounded-2xl grad-bg text-white">
                  <c.icon size={20} />
                </span>
                <h3 className="mt-5 font-heading text-xl font-medium tracking-tight text-aura-ink">{c.t}</h3>
                <p className="mt-2 text-sm leading-6 text-aura-text2">{c.d}</p>
                <span className="mt-4 inline-block text-sm font-semibold text-aura-pink group-hover:text-aura-pinkHover">
                  {CONTACT_EMAIL}
                </span>
              </motion.a>
            ))}
          </div>

          <p className="mt-12 text-center text-sm text-aura-muted">
            We aim to respond within a reasonable time. For urgent safety issues,
            please use in-app report and block tools.
          </p>
        </div>
      </section>
    </div>
  );
}
